import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Settings2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const EMPTY_DEFAULTS = {
  country_of_origin: "",
  mfd_pkd_by: "",
  connect_with_us: "",
  marketed_by: "",
  query_email: "",
  customer_care: "",
};

const EMPTY = {
  copper: { ...EMPTY_DEFAULTS },
  copper_customize: { ...EMPTY_DEFAULTS },
  india: { ...EMPTY_DEFAULTS },
  china: { ...EMPTY_DEFAULTS },
};

const TABS = [
  { key: "copper", label: "Attro Copper", desc: "2.5 × 2.5 in sticker defaults" },
  { key: "copper_customize", label: "Copper Customize", desc: "2.5 × 2.5 in · MRP struck-through + MOP defaults" },
  { key: "india", label: "Attro India", desc: "3 × 1.5 in — Packed & Marketed defaults" },
  { key: "china", label: "Attro China", desc: "3 × 1.5 in — Imported & Marketed defaults" },
];

export default function SettingsPage() {
  const [settings, setSettings] = useState(EMPTY);
  const [active, setActive] = useState("copper");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings");
        setSettings({
          copper: { ...EMPTY_DEFAULTS, ...(data.copper || {}) },
          copper_customize: { ...EMPTY_DEFAULTS, ...(data.copper_customize || {}) },
          india: { ...EMPTY_DEFAULTS, ...(data.india || {}) },
          china: { ...EMPTY_DEFAULTS, ...(data.china || {}) },
        });
      } catch {
        toast.error("Failed to load settings");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const setField = (kind, key, value) => {
    setSettings((s) => ({ ...s, [kind]: { ...s[kind], [key]: value } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", settings);
      toast.success("Defaults saved — new labels will auto-fill from here");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;

  const current = settings[active];
  const isCopper = active === "copper" || active === "copper_customize";

  return (
    <div className="px-8 py-7 max-w-5xl">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1.5 flex items-center gap-1.5">
            <Settings2 size={13} />
            Settings
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-zinc-950">Default label fields</h1>
          <p className="text-sm text-zinc-500 mt-1.5">
            Fill these once — they auto-populate empty fields on every new label of the selected type.
          </p>
        </div>
        <Button
          className="h-10 bg-zinc-950 text-white hover:bg-zinc-800 gap-2"
          onClick={save}
          disabled={saving}
          data-testid="save-settings-btn"
        >
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
          {saving ? "Saving…" : "Save defaults"}
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-white border border-zinc-200 rounded-md p-1 mb-5 w-fit" data-testid="settings-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setActive(t.key)}
            data-testid={`settings-tab-${t.key}`}
            className={`px-4 h-9 rounded text-[13px] font-medium transition-colors ${
              active === t.key
                ? "bg-zinc-950 text-white"
                : "text-zinc-600 hover:bg-zinc-100"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <section className="bg-white border border-zinc-200 rounded-md p-6" data-testid={`settings-panel-${active}`}>
        <div className="text-[13px] font-semibold text-zinc-900 mb-1">
          {TABS.find((t) => t.key === active).label} defaults
        </div>
        <div className="text-[11px] text-zinc-500 mb-5">
          {TABS.find((t) => t.key === active).desc}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <FieldRow label="Country of Origin">
            <Input
              value={current.country_of_origin}
              onChange={(e) => setField(active, "country_of_origin", e.target.value)}
              placeholder={active === "china" ? "China" : "India"}
              className="h-9"
              data-testid={`settings-${active}-country`}
            />
          </FieldRow>

          <FieldRow label="For any Query (email)">
            <Input
              value={current.query_email}
              onChange={(e) => setField(active, "query_email", e.target.value)}
              placeholder="customercare@amworld.com"
              className="h-9"
              data-testid={`settings-${active}-query-email`}
            />
          </FieldRow>

          <FieldRow label="Customer Care (phone)">
            <Input
              value={current.customer_care}
              onChange={(e) => setField(active, "customer_care", e.target.value)}
              placeholder="+91-7045352685"
              className="h-9"
              data-testid={`settings-${active}-customer-care`}
            />
          </FieldRow>
          <div />

          {isCopper ? (
            <>
              <FieldRow label="Mfd. & Pkd. by (address)" full>
                <Textarea
                  value={current.mfd_pkd_by}
                  onChange={(e) => setField(active, "mfd_pkd_by", e.target.value)}
                  placeholder="Company name and full address"
                  className="min-h-[80px] text-sm"
                  data-testid={`settings-${active}-mfd`}
                />
              </FieldRow>
              <FieldRow label="Connect with us" full>
                <Textarea
                  value={current.connect_with_us}
                  onChange={(e) => setField(active, "connect_with_us", e.target.value)}
                  placeholder="Contact address, email, phone"
                  className="min-h-[80px] text-sm"
                  data-testid={`settings-${active}-connect`}
                />
              </FieldRow>
            </>
          ) : (
            <FieldRow label={active === "china" ? "Imported & Marketed By (address)" : "Packed & Marketed By (address)"} full>
              <Textarea
                value={current.marketed_by}
                onChange={(e) => setField(active, "marketed_by", e.target.value)}
                placeholder="Company name and full address"
                className="min-h-[90px] text-sm"
                data-testid={`settings-${active}-marketed`}
              />
            </FieldRow>
          )}
        </div>

        <div className="mt-5 text-[11px] text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2">
          Auto-fill runs only when a field is empty on a new label — anything you type by hand always wins.
        </div>
      </section>
    </div>
  );
}

const FieldRow = ({ label, full, children }) => (
  <div className={`space-y-1.5 ${full ? "md:col-span-2" : ""}`}>
    <Label className="text-[11px] font-medium text-zinc-700 uppercase tracking-wider">
      {label}
    </Label>
    {children}
  </div>
);
